<template>
  <div class="bref-container">
    <div class="main-column">
      <InputWithValidation
        v-model="localPitch"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        class="pitch"
        :label="$t('projet.en-bref.pitch')"
        maxlength="2590"
        :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : 'required'"
        vid="bref_pitch"
        :disabled="readonly || !isPorteurProjet"
        type="textarea"
        @manual-change="$emit('trigger-validation')"
      />
      <div class="map">
        <div v-if="projetAnnuel.natureAireEducative === 'AME'" class="area-title">
          <img class="icon" src="/img/poisson.svg" alt="Poisson" />
          {{ $t("projet.en-bref.AME") }}
        </div>
        <div v-else class="area-title">
          <img class="icon" src="/img/scarabee.svg" alt="Scarabée" />
          {{ $t("projet.en-bref.ATE") }}
        </div>

        <!-- WARNING : it's a vee-validate hack -->
        <div class="vee-validate-hack">
          <InputWithValidation
            v-if="!projetAnnuel.zoneGeographique || projetAnnuel.zoneGeographique == 'null'"
            :validation-activated="validationActivated"
            class="mt-3 MapDrawView-invisible-input"
            type="textarea"
            vid="bref_map"
            rules="required"
          />
        </div>

        <MapDrawView
          v-if="shouldCreateMap"
          :key="projetAnnuel.zoneGeographique"
          :validation-activated="validationActivated"
          :show-validation-errors="showValidationErrors"
          :map-has-focus="true"
          :zone-geographique="projetAnnuel.zoneGeographique"
          :etablissement="projetAnnuel.etablissements[0] || null"
          :readonly="readonly || !isPorteurProjet"
          @zone-update="changeZoneGeographique"
        />
        <MapDrawView
          v-if="mapScreenShotRequested"
          :validation-activated="false"
          :show-validation-errors="false"
          :map-has-focus="true"
          :zone-geographique="projetAnnuel.zoneGeographique"
          :etablissement="projetAnnuel.etablissements[0] || null"
          :readonly="true"
          :map-screen-shot-requested="true"
          @map-screenshot-done="(dataURL) => $emit('map-screenshot-done', dataURL)"
          @zone-update="changeZoneGeographique"
        />

        <br />

        <InputWithValidation
          v-model="localChoixAiresEducatives"
          :validation-activated="validationActivated"
          :show-validation-errors="showValidationErrors"
          :rules="
            projetAnnuel.zoneGeographiqueChanged ||
            projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT_LIGHT'
              ? 'required'
              : ''
          "
          type="textarea"
          :label="$t('projetPage.infoAdministratives.site.choix')"
          :input-notice="$t('projetPage.infoAdministratives.site.choix-message')"
          maxlength="1400"
          :disabled="readonly || !isPorteurProjet"
          vid="site_choixAiresEducatives"
          @manual-change="$emit('trigger-validation')"
        />
      </div>
    </div>
    <div id="carousel-gallery-column" class="carousel-gallery-column">
      <CarouselView
        v-if="projetAnnuel.id"
        :projet-annuel-id="projetAnnuel.id"
        :images="images"
        :images-url="imagesUrl"
        :autorisation-droit-image-file-present="projetAnnuel.autorisationDroitImageFilePresent"
        :readonly="readonly"
        :is-porteur-projet="isPorteurProjet"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        @trigger-validation="$emit('trigger-validation')"
        @images-updated="updateImages"
        @autorisation-droit-image-updated="updateAutorisationDroitImage"
      ></CarouselView>
      <CalendrierView
        :projet-annuel="projetAnnuel"
        :readonly="readonly"
        :is-porteur-projet="isPorteurProjet"
      ></CalendrierView>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { FichierDTO, ProjetAnnuelDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import MapDrawView from "@/views/maps/MapDrawView.vue"
import CarouselView from "./CarouselView.vue"
import CalendrierView from "./CalendrierView.vue"
import { GraeService } from "@/services/rest/grae/GraeService"
import { FileService } from "@/services/rest/utils/FileService"
import Constants from "@/services/Constants"

export default defineComponent({
  name: "EnBrefView",
  components: { InputWithValidation, MapDrawView, CarouselView, CalendrierView },
  props: {
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isPorteurProjet: {
      type: Boolean,
      default: false,
    },
    validationActivated: {
      type: Boolean,
      default: false,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
    mapScreenShotRequested: {
      type: Boolean,
      required: false,
    },
    section: {
      type: String,
      default: "",
    },
  },

  emits: [
    "map-screenshot-done",
    "trigger-validation",
    "projet-annuel-updated",
    "images-url-changed",
  ],
  data() {
    return {
      // Wait for focus before creating map to avoid display issue
      shouldCreateMap: this.section === "bref",
      graeService: GraeService.INSTANCE,
      fileService: FileService.INSTANCE,
      contact: new UtilisateurDTO(),
      images: [] as FichierDTO[],
      imagesUrl: [] as string[],
    }
  },
  computed: {
    localPitch: {
      get(): string {
        return this.projetAnnuel.pitch || ""
      },
      set(value: string): void {
        this.$emit("projet-annuel-updated", {
          ...this.projetAnnuel,
          pitch: value,
        })
      },
    },
    localChoixAiresEducatives: {
      get(): string {
        return this.projetAnnuel.choixAiresEducatives || ""
      },
      set(value: string): void {
        this.$emit("projet-annuel-updated", {
          ...this.projetAnnuel,
          choixAiresEducatives: value,
        })
      },
    },
  },
  watch: {
    section() {
      this.activeSectionChanged()
    },
    "projetAnnuel.id": {
      handler(newId: string, oldId: string): void {
        if (newId && newId !== oldId) {
          this.loadImages()
        }
      },
      immediate: true,
    },
  },
  created(): void {
    if (this.projetAnnuel.grae.id) {
      this.loadContact()
    }
    this.shouldCreateMap = this.section === "bref"
  },
  methods: {
    activeSectionChanged(): void {
      this.shouldCreateMap = this.shouldCreateMap || this.section === "bref"
    },

    async loadContact(): Promise<void> {
      this.contact = await this.graeService.getContact(this.projetAnnuel.grae.id)
    },

    changeZoneGeographique(zoneGeographique: string): void {
      // Créer une copie pour éviter la mutation de prop
      const updatedProjet = { ...this.projetAnnuel }
      updatedProjet.zoneGeographiqueChanged = true
      updatedProjet.zoneGeographique = zoneGeographique

      // Émettre la mise à jour au parent
      this.$emit("projet-annuel-updated", updatedProjet)
      this.$nextTick(() => this.$emit("trigger-validation"))
    },

    async loadImages(): Promise<void> {
      if (!this.projetAnnuel.id) {
        return
      }

      this.images = await this.fileService.getFichiers(this.projetAnnuel.id, "IMAGE")

      if (this.images && this.images.length !== 0) {
        this.imagesUrl = this.images.map((image) =>
          Constants.apiUrlv2(`/projets/${this.projetAnnuel.id}/fichiers/${image.id}/content`),
        )
      } else {
        this.imagesUrl = []
      }

      this.$emit("images-url-changed", this.imagesUrl)
    },

    updateImages(images: FichierDTO[]): void {
      this.images = images

      if (this.images && this.images.length !== 0) {
        this.imagesUrl = this.images.map((image) =>
          Constants.apiUrlv2(`/projets/${this.projetAnnuel.id}/fichiers/${image.id}/content`),
        )
      } else {
        this.imagesUrl = []
      }

      this.$emit("images-url-changed", this.imagesUrl)
    },

    updateAutorisationDroitImage(value: boolean): void {
      const updatedProjet = { ...this.projetAnnuel }
      updatedProjet.autorisationDroitImageFilePresent = value
      this.$emit("projet-annuel-updated", updatedProjet)
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.bref-container {
  display: flex;
  //flex-wrap: wrap;
  gap: 30px;

  .main-column {
    flex-grow: 3;
  }

  .carousel-gallery-column {
    flex-grow: 3;
  }

  .actualite-column {
    flex-grow: 1;
  }

  .columns {
    margin-right: 0;
  }
}

.pitch {
  .textarea:not([rows]) {
    min-height: 15em !important;
    max-height: 150em !important;
  }
}

.map {
  margin-top: 35px;
}

.area-title {
  display: flex;
  align-items: center;
  color: #363636;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 15px;
}

.icon {
  width: 40px;
}

.border-left {
  border-left: 1px solid $bookmarkcard-border-color;
}

.pink {
  color: #cf1384;
}
</style>
